import { ComplianceCategoryCode, ComplianceOutcome } from '@di/servicing';

type ComplianceOutcomeLabelConfig = {
    color: string;
    label: string;
};

const OVERALL_COMPLIANCE_OUTCOME_LABELS: Record<ComplianceOutcome, ComplianceOutcomeLabelConfig> = {
    [ComplianceOutcome.Compliant]: {
        color: '#38A169',
        label: 'Compliant',
    },
    [ComplianceOutcome.NonCompliant]: {
        color: '#E53E3E',
        label: 'Non-compliant',
    },
    [ComplianceOutcome.Unknown]: {
        color: '#000',
        label: 'Unknown',
    },
};

const COMPLIANCE_CATEGORY_LABELS: Record<ComplianceCategoryCode, string> = {
    [ComplianceCategoryCode.CompliantStar]: 'Compliant*',
    [ComplianceCategoryCode.Compliant]: 'Compliant',
    [ComplianceCategoryCode.NonCompliant]: 'Non-compliant',
    [ComplianceCategoryCode.PendingItems]: 'Pending items',
    [ComplianceCategoryCode.Recommendation]: 'Recommendation',
    [ComplianceCategoryCode.Unknown]: 'Unknown',
};

type ServiceOutcomeInput = {
    overallComplianceOutcome: ComplianceOutcome;
    highestComplianceCategoryCode: ComplianceCategoryCode;
};

function getOverallComplianceOutcomeLabel(overallComplianceOutcome: ComplianceOutcome): string {
    return OVERALL_COMPLIANCE_OUTCOME_LABELS[overallComplianceOutcome].label;
}

function getOverallComplianceOutcomeColor(overallComplianceOutcome: ComplianceOutcome): string {
    return OVERALL_COMPLIANCE_OUTCOME_LABELS[overallComplianceOutcome].color;
}

function getComplianceCategoryLabel(highestComplianceCategoryCode: ComplianceCategoryCode): string {
    return COMPLIANCE_CATEGORY_LABELS[highestComplianceCategoryCode];
}

function getServiceOutcomeLabelColor({
    overallComplianceOutcome,
    highestComplianceCategoryCode,
}: ServiceOutcomeInput): string {
    if (
        highestComplianceCategoryCode === ComplianceCategoryCode.Compliant
        || highestComplianceCategoryCode === ComplianceCategoryCode.CompliantStar
    ) {
        return '#38A169';
    }

    if (highestComplianceCategoryCode === ComplianceCategoryCode.NonCompliant) {
        return '#E53E3E';
    }

    return getOverallComplianceOutcomeColor(overallComplianceOutcome);
}

function getServiceOutcomeLabel({
    overallComplianceOutcome,
    highestComplianceCategoryCode,
}: ServiceOutcomeInput): string | null {
    if (
        overallComplianceOutcome === ComplianceOutcome.Unknown
        || highestComplianceCategoryCode === ComplianceCategoryCode.Unknown
    ) {
        return null;
    }

    if (highestComplianceCategoryCode === ComplianceCategoryCode.Compliant) {
        return 'Compliant';
    }

    if (highestComplianceCategoryCode === ComplianceCategoryCode.NonCompliant) {
        return 'Non-compliant';
    }

    if (highestComplianceCategoryCode === ComplianceCategoryCode.CompliantStar) {
        return 'Compliant*';
    }

    return `${getOverallComplianceOutcomeLabel(overallComplianceOutcome)} - ${getComplianceCategoryLabel(highestComplianceCategoryCode)}`;
}

function getServiceOutcomeLabelWithColour(input: ServiceOutcomeInput): string | null {
    const label = getServiceOutcomeLabel(input);
    if (label === null) {
        return null;
    }

    const color = getServiceOutcomeLabelColor(input);
    return `<span style="color:${color}; font-weight:500">${label}</span>`;
}

export {
    getServiceOutcomeLabel,
    getServiceOutcomeLabelWithColour,
};
